import React from 'react';
import defaults from './default-versions.json';

import { PersonNode as PersonNodeV1 } from '../../domains/genogram/components/PersonNode';
import { FrameRenderer as FrameV1 } from '../../components/canvas/FrameRenderer';
import { StickyNoteNode } from '../../components/canvas/StickyNoteNode';
import { BasicShapeNode } from '../../domains/diagram/components/basic/BasicShapeNode';
import { FlowchartShapeNode } from '../../domains/diagram/components/flowchart/FlowchartShapeNode';
import { BpmnShapeNode } from '../../domains/diagram/components/bpmn/BpmnShapeNode';
import { OrgChartShapeNode } from '../../domains/diagram/components/org/OrgChartShapeNode';
import { TableShapeNode } from '../../domains/diagram/components/table/TableShapeNode';
import { PositionNode } from '../../domains/orgchart/components/PositionNode';
import { PositionStaffingNode } from '../../domains/orgchart/components/PositionStaffingNode';
import { OrgPersonNode } from '../../domains/orgchart/components/OrgPersonNode';
import { DepartmentNode } from '../../domains/orgchart/components/DepartmentNode';

type Kind = 'shape' | 'connector' | 'frame';

type ComponentType = React.ComponentType<any>;

const registry: Record<Kind, Record<string, Record<number, ComponentType>>> = {
  shape: {
    person: {
      1: PersonNodeV1 as unknown as ComponentType,
    },
    'sticky-note': {
      1: StickyNoteNode as unknown as ComponentType,
    },
    'basic-shape': {
      1: BasicShapeNode as unknown as ComponentType,
    },
    'flowchart-shape': {
      1: FlowchartShapeNode as unknown as ComponentType,
    },
    'bpmn-shape': {
      1: BpmnShapeNode as unknown as ComponentType,
    },
    'org-shape': {
      1: OrgChartShapeNode as unknown as ComponentType,
    },
    table: {
      1: TableShapeNode as unknown as ComponentType,
    },
    'org_position': {
      1: PositionNode as unknown as ComponentType,
      2: PositionStaffingNode as unknown as ComponentType,
    },
    'org_person': {
      1: OrgPersonNode as unknown as ComponentType,
    },
    'org_department': {
      1: DepartmentNode as unknown as ComponentType,
    },
  },
  connector: {
    // No versioned connector components yet. Manifests handle visuals.
  },
  frame: {
    frame: {
      1: FrameV1 as unknown as ComponentType,
    },
  },
};

export const VersionedComponentRegistry = {
  registerComponent(kind: Kind, type: string, version: number, component: ComponentType) {
    if (!registry[kind][type]) {
      registry[kind][type] = {};
    }
    registry[kind][type][version] = component;
  },

  getDefaultVersion(type: string, kind: Kind = 'shape'): number {
    const byKind: any = (defaults as any)[kind];
    const v = byKind ? byKind[type] : undefined;
    if (typeof v === 'number') return v;
    // Back-compat with old flat structure
    const flat = (defaults as any)[type];
    return typeof flat === 'number' ? flat : 1;
  },

  getComponent(type: string, version?: number, kind: Kind = 'shape'): ComponentType | null {
    const byType = registry[kind]?.[type];
    if (!byType) return null;
    const v = (version ?? this.getDefaultVersion(type, kind)) as number;
    return byType[v] || byType[this.getDefaultVersion(type, kind)] || null;
  },
};

export default VersionedComponentRegistry;
